/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Radio } from 'antd'
import Text from 'src/components/ts-components/Text'
import {
  PAYMENT_PLANS,
  PaymentPlan,
  PaymentPlanOptions,
} from '../utils/paymentPlan'

interface PaymentPlanSelectorProps {
  options: PaymentPlanOptions
  selectedPlan: PaymentPlan
  onChange: (plan: PaymentPlan) => void
  disabled?: boolean
  formatAmount: (amount: number) => string
}

/**
 * "Choose a Payment Plan" card (Figma 19522:12531): Deposit Only vs Pay in Full.
 * Rendered above the payment method selector on deposit/installment divisions.
 */
const PaymentPlanSelector = ({
  options,
  selectedPlan,
  onChange,
  disabled = false,
  formatAmount,
}: PaymentPlanSelectorProps) => {
  return (
    <div css={containerStyle}>
      <Text variant='paragraphMedium' color={colors.WHITE} weight='bold'>
        Choose a Payment Plan
      </Text>
      <div css={separatorStyle} />
      <Radio.Group
        css={radioGroupStyle}
        value={selectedPlan}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <Radio value={PAYMENT_PLANS.DEPOSIT_ONLY}>
          <div css={optionContainerStyle(disabled)}>
            <div css={textContainerStyle}>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Deposit Only
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Pay only {formatAmount(options.depositAmount)} today
              </Text>
            </div>
          </div>
        </Radio>
        <Radio value={PAYMENT_PLANS.PAY_IN_FULL}>
          <div css={optionContainerStyle(disabled)}>
            <div css={textContainerStyle}>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Pay in Full
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                Pay {formatAmount(options.fullAmount)} today
              </Text>
            </div>
          </div>
        </Radio>
      </Radio.Group>
    </div>
  )
}

const containerStyle = css`
  display: flex;
  padding: 16px;
  background: ${colors.NEUTRAL_600};
  border-radius: 8px;
  border: 1px solid ${colors.NEUTRAL_300};
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  width: 100%;
  margin-bottom: 16px;
`

const separatorStyle = css`
  align-self: stretch;
  height: 0;
  border: 0.5px solid ${colors.NEUTRAL_400};
`

// Radio styling intentionally mirrors PaymentMethodSelector's so the two
// selectors that sit together on the payment step render identical controls
// (same size + checked ring/dot). Do not re-introduce an `.ant-radio-inner`
// size override here — a larger inner made the checked state read as a solid
// disc that didn't match the Credit Card / Defer Payment radios below.
const radioGroupStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-radio-wrapper {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    width: 100%;
    margin: 0;
    padding: 0;

    > span:last-child {
      flex: 1;
    }
  }

  .ant-radio {
    display: flex;
    flex-shrink: 0;
  }

  .ant-radio-wrapper-disabled {
    cursor: not-allowed;
  }
`

const optionContainerStyle = (disabled: boolean) => css`
  width: 100%;
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  display: flex;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: ${disabled ? 0.5 : 1};
`

const textContainerStyle = css`
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  display: flex;
`

export default PaymentPlanSelector
